// src/pages/YTDCommissions.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { format, getYear, getMonth, startOfYear } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TEAL = '#00d4a0';
const TT = { background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, fontSize: 11, color: '#fff' };

export default function YTDCommissions() {
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedRep, setSelectedRep] = useState('all');

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  const yearOrders = orders.filter(o => {
    if (!o.date || o.date === '' || o.date === '1901-01-01') return false;
    const d = new Date(o.date);
    if (isNaN(d)) return false;
    return getYear(d) === year && (selectedRep === 'all' || o.repId === selectedRep || o.repName === selectedRep);
  });

  // Monthly breakdown
  const monthlyData = MONTHS.map((month, i) => {
    const monthOrders = yearOrders.filter(o => { const d = new Date(o.date); return !isNaN(d) && getMonth(d) === i; });
    const commission = monthOrders.reduce((a, o) => a + (o.totalCommission || 0), 0);
    const deals = monthOrders.length;
    const completed = monthOrders.filter(o => o.status === 'Completed').length;
    return { month, commission: Math.round(commission), deals, completed };
  });

  const ytdTotal = monthlyData.reduce((a, m) => a + m.commission, 0);
  const ytdDeals = monthlyData.reduce((a, m) => a + m.deals, 0);
  const ytdCompleted = monthlyData.reduce((a, m) => a + m.completed, 0);
  const currentMonthIdx = year === new Date().getFullYear() ? new Date().getMonth() : 11;
  const currentMonth = monthlyData[currentMonthIdx];

  // Rep breakdown for the year
  const repMap = {};
  yearOrders.forEach(o => {
    const name = o.repName || 'Unknown';
    if (!repMap[name]) repMap[name] = { name, commission: 0, deals: 0, completed: 0 };
    repMap[name].commission += o.totalCommission || 0;
    repMap[name].deals++;
    if (o.status === 'Completed') repMap[name].completed++;
  });
  const repList = Object.values(repMap).sort((a, b) => b.commission - a.commission);

  const years = [];
  const validYears = orders.filter(o => o.date && o.date !== '').map(o => getYear(new Date(o.date))).filter(y => !isNaN(y) && y > 2000);
  const minYear = validYears.length > 0 ? Math.min(...validYears) : year;
  for (let y = new Date().getFullYear(); y >= minYear; y--) years.push(y);

  function exportCSV() {
    const lines = ['Month,Deals,Completed,Commission'];
    monthlyData.forEach(m => lines.push(`${m.month},${m.deals},${m.completed},$${m.commission}`));
    lines.push(`YTD Total,${ytdDeals},${ytdCompleted},$${Math.round(ytdTotal)}`);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `ytd_commissions_${year}.csv`; a.click();
  }

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>YTD commissions</h1>
          <p style={S.sub}>Year to date · {year}</p>
        </div>
        <div style={S.controls}>
          <select style={S.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select style={S.select} value={selectedRep} onChange={e => setSelectedRep(e.target.value)}>
            <option value="all">All reps</option>
            {reps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <button style={S.exportBtn} onClick={exportCSV}>Export CSV</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={S.metrics}>
        {[
          { label: 'YTD commission', value: '$' + Math.round(ytdTotal).toLocaleString(), color: '#f5a623' },
          { label: 'YTD deals', value: ytdDeals.toLocaleString(), color: '#fff' },
          { label: 'YTD completed', value: ytdCompleted.toLocaleString(), color: TEAL },
          { label: `${MONTHS[currentMonthIdx]} commission`, value: '$' + (currentMonth?.commission || 0).toLocaleString(), color: '#5599cc' },
        ].map(m => (
          <div key={m.label} style={S.metricCard}>
            <div style={S.metricLabel}>{m.label}</div>
            <div style={{ ...S.metricValue, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Monthly charts */}
      <div style={S.charts}>
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Monthly commission</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fill: '#555', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={v => '$' + (v/1000).toFixed(0) + 'k'} />
              <Tooltip contentStyle={TT} formatter={v => ['$' + v.toLocaleString(), 'Commission']} />
              <Bar dataKey="commission" fill="#f5a623" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Monthly deals</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={monthlyData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fill: '#555', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TT} />
              <Line type="monotone" dataKey="deals" stroke={TEAL} strokeWidth={2} dot={false} name="Written" />
              <Line type="monotone" dataKey="completed" stroke="#5599cc" strokeWidth={2} dot={false} name="Completed" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly table */}
      <div style={S.tableCard}>
        <div style={S.chartTitle}>Monthly breakdown</div>
        <table style={S.table}>
          <thead>
            <tr>{['Month', 'Written', 'Completed', 'Cancelled', 'Commission', 'Avg/deal'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {monthlyData.map((m, i) => {
              const monthOrders = yearOrders.filter(o => {
          const d = new Date(o.date);
          return getMonth(d) === i;
        });
              const cancelled = monthOrders.filter(o => o.status === 'Cancelled').length;
              const avg = m.deals ? Math.round(m.commission / m.deals) : 0;
              return (
                <tr key={m.month} style={{ background: i === currentMonthIdx && year === new Date().getFullYear() ? '#1a1a24' : 'transparent' }}>
                  <td style={{ ...S.td, fontWeight: i === currentMonthIdx && year === new Date().getFullYear() ? 600 : 400 }}>{m.month} {year}</td>
                  <td style={S.td}>{m.deals}</td>
                  <td style={{ ...S.td, color: TEAL }}>{m.completed}</td>
                  <td style={{ ...S.td, color: cancelled > 0 ? '#ff6b6b' : '#555' }}>{cancelled}</td>
                  <td style={{ ...S.td, color: '#f5a623' }}>${m.commission.toLocaleString()}</td>
                  <td style={{ ...S.td, color: '#888' }}>${avg.toLocaleString()}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: '2px solid #2a2a3a' }}>
              <td style={{ ...S.td, fontWeight: 600, color: '#fff' }}>YTD Total</td>
              <td style={{ ...S.td, fontWeight: 600 }}>{ytdDeals}</td>
              <td style={{ ...S.td, fontWeight: 600, color: TEAL }}>{ytdCompleted}</td>
              <td style={S.td}>—</td>
              <td style={{ ...S.td, fontWeight: 600, color: '#f5a623' }}>${Math.round(ytdTotal).toLocaleString()}</td>
              <td style={{ ...S.td, color: '#888' }}>${ytdDeals ? Math.round(ytdTotal / ytdDeals).toLocaleString() : 0}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  controls: { display: 'flex', gap: 8, alignItems: 'center' },
  select: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 8, padding: '7px 12px', color: '#ccc', fontSize: 12, outline: 'none' },
  exportBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 },
  metricCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 18px' },
  metricLabel: { fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  metricValue: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px' },
  charts: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  chartCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 18px' },
  chartTitle: { fontSize: 10, color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 18px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '7px 12px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '10px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
};
